import { LitElement, html } from 'lit';
import { styles } from './css';
import { externalStyles } from './external-css';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { imagebossUrl } from '@Helpers/imageboss';
import {
  externalNgLoader,
  logErrorToSentry,
  showExternalSuspend,
  showExternalPointSupport,
} from '../../../../helpers/helpers';
import { fireAdobeAnalyticsEvent } from '../../../../helpers/adobe-analytics';

import '@Root/lib/lit-3form-block/components/cta-text';

import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';

import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '@Root/lib/lit-styles.js';

const getInit = { method: 'GET', headers: { Accept: 'application/json' } };

export class CustomizeProductBarrel extends LitElement {
  static get properties() {
    return {
      content: { type: Object },
      _expanded: { type: Boolean },
      _selectedHardwareFinish: { type: Object },
      _selectedMaterial: { type: Object },
      _selectedPattern: { type: Object },
      _currentPatternSwatches: { type: Array },
      _hardwareOptions: { type: Array },
      _filterOptions: { type: Array },
      _filterUrlQueryString: { type: String },
      _totalPages: { type: Number },
      _isLoadingCurrentResults: { type: Boolean },
      _searchString: { type: String },
      _userInvokedSearch: { type: String },
      _reportedSearchString: { type: String },
      _currentPagePath: { type: String },
      _currentPageNumber: { type: Number },
      _loadedPages: { type: Object },
      _hasLoadedInitialOptions: { type: Boolean },
      _hideHardwareOptions: { type: Boolean },
      _externalNgReady: { type: Boolean },
      _renderExternalOSSComponent: { type: Boolean },
      _externalNgSystem: { type: String },
      _technicalDocumentIds: { type: String },
      _renderedImageUrl: { type: String },
      _standardLaminateId: { type: String },
      _selectFirstOptionOnLoad: { type: Boolean },
      _selectedFirstOptionOnLoad: { type: Boolean },
      _doingSearchLookup: { type: Boolean },
      _hasRenderInterlayerId: { type: Boolean },
      _updatingMaterial: { type: Boolean },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      styles,
      externalStyles,
    ];
  }

  constructor() {
    super();

    this.content = JSON.parse(this.getAttribute('content'));

    this._totalPages = 0;
    this._lastFetchedPagePath = '';
    this._selectedHardwareFinish = {};
    this._selectedPattern = {};
    this._selectedMaterial = {};
    this._allPatternSwatches = [];
    this._currentPatternSwatches = [];
    this._hardwareOptions = [];
    this._filterOptions = [];
    this._filterUrlQueryString = '';
    this._isLoadingCurrentResults = false;
    this._searchString = '';
    this._currentPagePath = '';
    this._currentPageNumber = 1;
    this._loadedPages = {};
    this._hasLoadedInitialOptions = false;
    this._hideHardwareOptions = false;
    this._externalNgReady = false;
    this._renderExternalOSSComponent = false;
    this._technicalDocumentIds = '';
    this._renderedImageUrl = '';
    this._externalNgSystem = '';
    this._standardLaminateId = '';
    this.externalEventListenerIsSetup = false;
    this._userInvokedSearch = false;
    this._reportedSearchString = '';
    this._selectFirstOptionOnLoad = false;
    this._selectedFirstOptionOnLoad = false;
    this._doingSearchLookup = false;
    this._hasRenderInterlayerId = false;
    this._updatingMaterial = false;
    this.debounceTimer = undefined;

    this.addEventListener('patternFilterUpdated', e => {
      this.updateFilterSelection(e);
    });
    this.addEventListener('searchFromFilterUpdated', e => {
      if (typeof e?.detail?.inputValue === 'string') {
        this.updateSearchSelection(e.detail.inputValue, true);
      }
    });
  }

  async connectedCallback() {
    super.connectedCallback();
    this._expanded = false;
    this._selectedMaterial = this.materialOptions[0];

    this.addEventListener('scrollToVisualize', e => {
      this.changeBarrelVisibilty('open');
    });

    try {
      const renderInterlayerId =
        new URLSearchParams(window.location.search).get(
          'render_interlayer_id'
        ) || null;
      this._hasRenderInterlayerId = !!renderInterlayerId;
      await this.fetchThreekitHardwareOptions(renderInterlayerId);
    } catch (error) {
      logErrorToSentry(error);
    }

    // Barrels default to be closed on page load. This one is the exception.
    this.changeBarrelVisibilty('open');

    this.checkExternalNG();
  }

  render() {
    return html`
      <div
        class=${classMap({
          'barrel-wrapper': true,
          expanded: this._expanded,
          'print-none': this.hideInPrint,
        })}
      >
        <div
          id="visualization-barrel-header"
          class=${classMap({
            'barrel-header': true,
            hide: this._expanded,
          })}
          @click="${this.changeBarrelVisibilty}"
        >
          <span class="barrel-heavy-text">
            <span>DESIGN YOUR OWN</span>
          </span>
          <span class="barrel-header-icon print-none">
            <img src=${this.barrelHeaderIcon} />
          </span>
        </div>
        ${this.showHardwareOptions && this._hasRenderInterlayerId
          ? this.hardwareOptionsHTML
          : html``}
        ${this.patternOptionsHTML}
        <!-- swap the display order if a renderInterlayerId is present -->
        ${this.showHardwareOptions && !this._hasRenderInterlayerId
          ? this.hardwareOptionsHTML
          : html``}
        ${this._renderExternalOSSComponent && !!this._standardLaminateId
          ? html`
              <external-online-simple-spec
                image-url=${this._renderedImageUrl}
                technical-document-ids="[${this._technicalDocumentIds}]"
                input-form=${JSON.stringify(this.externalNgFormObject)}
              ></external-online-simple-spec>
              <p>
                Need help or have more custom options?
                <u>
                  <a href="/request-quote" target="_blank" autofocus="false">
                    Talk with a consultant.
                  </a>
                </u>
              </p>
            `
          : html`
              <cta-button
                cta-data=${this.primaryCtaData}
                cta-type="primary"
                @click="${this.requestAQuote}"
              ></cta-button>
            `}
      </div>
    `;
  }

  async setupExternalAnalyticsListener() {
    if (!this.externalEventListenerIsSetup) {
      const checkInterval = setInterval(() => {
        const externalOSSElement = this.shadowRoot.querySelector(
          'external-online-simple-spec'
        );
        if (externalOSSElement) {
          externalOSSElement.addEventListener('submitted', e => {
            if (typeof gtag !== 'function') return;
            const detail = e.detail;
            if (!!detail) {
              const price = detail?.price,
                system = this._externalNgSystem;

              gtag('event', 'Specification Created', {
                event_category: 'embedded application',
                event_label: system,
                value: price,
              });
            }
          });
          this.externalEventListenerIsSetup = true;

          clearInterval(checkInterval);
        }
      }, 20);
    }
  }

  async checkExternalNG() {
    // only proceed if externalNGData was given
    if (!this.content.externalNGData) return;

    if (!window.externalNgLoaded) await externalNgLoader();
    const externalNGData = this.content.externalNGData || {};
    this._technicalDocumentIds = externalNGData.technicalDocumentIds;
    this._renderedImageUrl = externalNGData.renderedImageUrl;
    this._externalNgSystem = externalNGData.system;
    this.addEventListener('renderedProductImageDidChange', e => {
      this._renderedImageUrl = e?.detail?.camera1ImageUrl;
    });

    this._externalNgReady = true;

    switch (this._externalNgSystem) {
      case 'Point Support':
        this._renderExternalOSSComponent = showExternalPointSupport();
      case 'Suspend':
        this._renderExternalOSSComponent = showExternalSuspend();
    }

    this.requestUpdate();
  }

  get externalNgFormObject() {
    const inputForm = {
      system: this._externalNgSystem,
      laminateId: this._standardLaminateId,
      config: {},
    };

    if (
      this._externalNgSystem === 'Suspend' &&
      !!this._selectedHardwareFinish
    ) {
      let hardwareFinish = this._selectedHardwareFinish
        ? this.configuredSwatchName(this._selectedHardwareFinish)
        : '';

      hardwareFinish =
        hardwareFinish === 'Stainless' ? 'Stainless Steel' : hardwareFinish;

      if (hardwareFinish) inputForm.config.hardwareFinish = hardwareFinish;
    }

    return inputForm;
  }

  get showHardwareOptions() {
    return this._hardwareOptions.length > 0;
  }

  get showPatternOptions() {
    return (
      !!this._currentPatternSwatches && this._currentPatternSwatches.length > 0
    );
  }

  get barrelHeaderIcon() {
    return this._expanded
      ? '/static/icons/SVG/minimize-dash.svg'
      : '/static/icons/SVG/expand-plus.svg';
  }

  get hideInPrint() {
    return this.content.hide_in_print;
  }

  changeBarrelVisibilty(override = '') {
    switch (override) {
      case 'open':
        this._expanded = true;
        break;
      case 'close':
        this._expanded = false;
        break;
      default:
        this._expanded = !this._expanded;
        break;
    }

    if (this._expanded) {
      if (this._externalNgReady && !this._selectedPattern.name) {
        this.updateSelectedPattern(
          this._loadedPages[this._currentPagePath]?.results[0]
        );
      }
      this.sendBarrelViewAnalyticsEvent();
    }
  }

  requestAQuote() {
    const requestMoreInfoFromBarrel = new CustomEvent(
      'requestMoreInfoFromBarrel',
      {
        bubbles: true,
        composed: true,
        detail: {
          cta: 'Request a Quote',
          url: '/request-quote',
        },
      }
    );
    this.dispatchEvent(requestMoreInfoFromBarrel);
    this.sendBarrelRequestQuoteAnalyticsEvent();
  }

  get primaryCtaData() {
    return JSON.stringify({
      text: 'Request a Quote',
      url: '/request-quote',
      fullWidth: true,
    });
  }

  get hardwareOptionsHTML() {
    return this._hideHardwareOptions
      ? html``
      : html`
          <div class="hardware-options-wrapper">
            <div class="hardware-options-header">
              <p class="barrel-heavy-text">
                Hardware Finishes
              </p>
            </div>
            <div class="swatch-group">
              <p class="swatch-group-header">
                Standard Finishes
              </p>
              <div class="swatches">
                ${this._hardwareOptions.map(option => {
                  if (option.isStandard) {
                    return this.renderOptionsSwatch(option);
                  }
                })}
              </div>
            </div>
            <div class="swatch-group">
              <p class="swatch-group-header">
                Premium Finishes (Additional Cost)
              </p>
              <div class="swatches">
                ${this._hardwareOptions.map(option => {
                  if (!option.isStandard) {
                    return this.renderOptionsSwatch(option);
                  }
                })}
              </div>
            </div>
          </div>
        `;
  }

  renderOptionsSwatch(option) {
    if (option.hex_code) {
      // Color Swatches
      return html`
        <div
          class="swatch-wrapper"
          @click="${() => this.updateSelectedPattern(option)}"
        >
          <span
            class=${classMap({
              'color-swatch': true,
              'selected-swatch': this.isSelectedPattern(option),
              'not-selected-swatch': !this.isSelectedPattern(option),
            })}
          >
            <span
              style="${styleMap(this.swatchBackgroundStyle(option))}"
            ></span>
          </span>
          <p class="swatch-name">${this.configuredSwatchName(option)}</p>
        </div>
      `;
    }
    if (option.imgSrc) {
      // Interlayer Swatches
      return html`
        <div
          class="swatch-wrapper"
          @click="${() => this.updateSelectedPattern(option)}"
        >
          <span
            class=${classMap({
              'image-swatch': true,
              'selected-swatch': this.isSelectedPattern(option),
              'not-selected-swatch': !this.isSelectedPattern(option),
            })}
          >
            <img
              src=${imagebossUrl(option.imgSrc, { height: 250, width: 200 })}
            />
          </span>
          <p class="swatch-name">${this.configuredSwatchName(option)}</p>
        </div>
      `;
    }
    if (option.color) {
      // Hardware Swatches
      return html`
        <div
          class="swatch-wrapper"
          @click="${() => this.updateSelectedHardwareFinish(option, true)}"
        >
          <span
            class=${classMap({
              'color-swatch': true,
              'selected-swatch': this.isSelectedHardwareFinish(option),
              'not-selected-swatch': !this.isSelectedHardwareFinish(option),
            })}
          >
            <span
              class=${classMap({
                'needs-border': option.needsBorder,
              })}
              style="${styleMap(this.swatchBackgroundStyle(option))}"
            ></span>
          </span>
          <p class="swatch-name">${this.configuredSwatchName(option)}</p>
        </div>
      `;
    }
  }

  configuredMaterialName(material) {
    return material.filter_option_text
      ? material.filter_option_text
      : material.slug[0].toUpperCase() + material.slug.slice(1);
  }

  configuredSwatchName(swatch) {
    if (swatch.display_name) {
      return swatch.display_name;
    } else {
      const name = swatch.name ? swatch.name : '';
      return swatch.parentName ? `${swatch.parentName} ${name}` : `${name}`;
    }
  }

  isSelectedMaterial(material) {
    return (
      !!this._selectedMaterial &&
      Object.keys(this._selectedMaterial).length > 0 &&
      material.imgSrc === this._selectedMaterial.imgSrc
    );
  }

  isSelectedPattern(option) {
    return (
      Object.keys(this._selectedHardwareFinish).length > 0 &&
      option.render_interlayer_id ===
        this._selectedPattern.render_interlayer_id &&
      option.imgSrc === this._selectedPattern.imgSrc
    );
  }

  isSelectedHardwareFinish(option) {
    return (
      Object.keys(this._selectedHardwareFinish).length > 0 &&
      option.id === this._selectedHardwareFinish.id
    );
  }

  get materialOptions() {
    const materialImages = {
      varia:
        'https://img.imageboss.me/mkt-images-prod/:dimensions/a4fbv52l7n1j2xylsirm1ygvrugr',
      glass:
        'https://img.imageboss.me/mkt-images-prod/:dimensions/k0qpit8vs6j4doq3iuzc8qt80noo',
    };
    const materialOptions = [];
    if (this.content.sample_types?.length > 0) {
      this.content.sample_types?.forEach(sampleType => {
        sampleType.imgSrc = materialImages[sampleType.slug];
        materialOptions.push(sampleType);
      });
    }
    // put Varia first in the list
    materialOptions.sort((a, b) => {
      return (b.slug === 'varia') - (a.slug === 'varia');
    });
    return materialOptions;
  }

  updateSelectedMaterial(material) {
    // only fire if new material is selected
    if (!material || material.id === this._selectedMaterial.id) return;
    this._updatingMaterial = true;

    // selecting a material should reset filters and go back to page 1 and
    // clear the currently selected pattern
    this.clearFilterSelections();
    this._currentPageNumber = 1;
    this._selectedPattern = {};

    this._selectedMaterial = material;
    this.fetchThreekitHardwareOptions();
    const visualizedMaterialSelectedEvent = new CustomEvent(
      'visualizedMaterialSelected',
      {
        detail: {
          material,
        },
        bubbles: true,
        composed: true,
      }
    );
    this.dispatchEvent(visualizedMaterialSelectedEvent);
    this.sendAdobeSelectionEvent(
      `Visualization|Select|Material-${material.filter_option_text}`
    );
  }

  updateSelectedPattern(option, sendAnalyticsEvent = true) {
    if (!option) return;
    option.threekit_stage_id = this.content.threekit_stage_id;
    option.sample_type_id = this._selectedMaterial.id;
    option.sample_type_display_name = this.configuredMaterialName(
      this._selectedMaterial
    );
    this._selectedPattern = option;
    this._standardLaminateId = this._selectedPattern.standard_laminate_id;
    const customPatternSelectedEvent = new CustomEvent(
      'customPatternSelected',
      {
        detail: {
          option,
        },
        bubbles: true,
        composed: true,
      }
    );
    this.dispatchEvent(customPatternSelectedEvent);
    if (sendAnalyticsEvent) {
      this.sendAdobeSelectionEvent(
        `Visualization|Select|Pattern-${this.configuredSwatchName(option)}`
      );
    }
  }

  updateSelectedHardwareFinish(option, sendAnalyticsEvent = false) {
    this._selectedHardwareFinish = option;
    const hardwareColorSelectedEvent = new CustomEvent(
      'hardwareColorSelected',
      {
        detail: {
          option,
        },
        bubbles: true,
        composed: true,
      }
    );
    this.fetchThreekitVariants(this._currentPageNumber);
    this.dispatchEvent(hardwareColorSelectedEvent);

    if (sendAnalyticsEvent) {
      // sometimes this gets set automatically, we only want to fire the event
      // if the user specifically selected the hardware color
      this.sendAdobeSelectionEvent(
        `Visualization|Select|Hardware-${this.configuredSwatchName(option)}`
      );
    }
    // If no pattern has been selected, auto select the first one in the list,
    // unless this is triggered from a material click
    if (
      Object.keys(this._selectedPattern).length === 0 &&
      !this._updatingMaterial
    ) {
      this.updateSelectedPattern(
        this._currentPatternSwatches[0],
        sendAnalyticsEvent
      );
    }
    this._updatingMaterial = false;
  }

  sendAdobeSelectionEvent(eventString) {
    const eventData = {
      type: 'tool',
      detail: {
        eVar35: eventString,
      },
    };

    fireAdobeAnalyticsEvent(eventData);
  }

  swatchBackgroundStyle(option) {
    const color = option.hex_code ? option.hex_code : option.color;
    return {
      'background-color': `#${color}`,
    };
  }

  get patternOptionsHTML() {
    return html`
      <div class="pattern-options-wrapper">
        ${this.content.sample_types?.length > 1
          ? html`
              <div class="pattern-options-header">
                <p class="barrel-heavy-text">
                  Material Type
                </p>
                <div class="material-swatches">
                  ${this.materialOptions.map(option => {
                    return html`
                      <div
                        class="swatch-wrapper"
                        @click="${() => this.updateSelectedMaterial(option)}"
                      >
                        <span
                          class=${classMap({
                            'image-swatch': true,
                            'selected-swatch': this.isSelectedMaterial(option),
                            'not-selected-swatch': !this.isSelectedMaterial(
                              option
                            ),
                          })}
                        >
                          <img
                            src=${imagebossUrl(option.imgSrc, {
                              height: 250,
                              width: 200,
                            })}
                          />
                        </span>
                        <p class="swatch-name">
                          ${this.configuredMaterialName(option)}
                        </p>
                      </div>
                    `;
                  })}
                </div>
              </div>
            `
          : html``}
        <div class="pattern-options-header">
          <p class="barrel-heavy-text">
            Colors and Patterns
          </p>
        </div>
        ${this.filterOptionsHTML}
        ${this.showPatternOptions
          ? html`
              <div class="swatches">
                ${this._currentPatternSwatches.map(option => {
                  return this.renderOptionsSwatch(option);
                })}
              </div>
              <div class="swatch-controls">
                <p class="counter description">${this.counterString}</p>
                <span class="swatch-page-controls">
                  <span
                    @click="${() => this.changeSwatchPage('previous')}"
                    class="left-arrow"
                  >
                    <img src="/static/icons/SVG/arrow-black-5x10.svg" />
                  </span>
                  <span
                    @click="${() => this.changeSwatchPage('next')}"
                    class="right-arrow"
                  >
                    <img src="/static/icons/SVG/arrow-black-5x10.svg" />
                  </span>
                </span>
              </div>
            `
          : html``}
        ${this._isLoadingCurrentResults
          ? html`
              <div class="no-results">
                <p class="description">Loading design options.</p>
              </div>
            `
          : html``}
        ${!this.showPatternOptions && !this._isLoadingCurrentResults
          ? html`
              <div class="no-results">
                <p class="description">No results found. Please try again.</p>
              </div>
            `
          : html``}
      </div>
    `;
  }

  get filterOptionsHTML() {
    if (this._filterOptions.length > 0) {
      return window.useMobileLayout
        ? html`
            <div class="pattern-filters print-none">
              <styled-multi-select
                options-data=${JSON.stringify(this._filterOptions)}
                data-include-search="true"
              ></styled-multi-select>
            </div>
          `
        : html`
            <div class="pattern-filters print-none">
              <div class="input-wrapper search">
                <input
                  type="text"
                  placeholder="Search by Name"
                  class="search-input"
                  .value="${this._searchString}"
                  @keyup=${e => {
                    const value = e.target.value;
                    clearTimeout(this.debounceTimer);
                    this.debounceTimer = setTimeout(() => {
                      this.updateSearchSelection(value);
                      this._userInvokedSearch = true;
                    }, 500);
                  }}
                />
                <img class="search-icon" src="/static/icons/SVG/search.svg" />
              </div>
              <styled-multi-select
                options-data=${JSON.stringify(this._filterOptions)}
              ></styled-multi-select>
            </div>
          `;
    } else {
      return html``;
    }
  }

  changeSwatchPage(direction) {
    const currentPageData = this._loadedPages[this._currentPagePath];

    if (currentPageData) {
      const pathToLoad = currentPageData[`${direction}_page_path`],
        pageToLoad = this._loadedPages[pathToLoad];

      this._currentPagePath = pathToLoad;

      if (pageToLoad === undefined) {
        this.loadVariantPage(pathToLoad)
          .then(json => {
            this._currentPageNumber = json.page_number;
            this.setVariantOptions(json, pathToLoad);
          })
          .then(() => {
            this.updateCurrentPatternSwatches();
          });
      } else {
        this._currentPageNumber = this._loadedPages[pathToLoad].page_number;
        this.updateCurrentPatternSwatches();
      }
    }
  }

  get swatchPagesCount() {
    return this._totalPages;
  }

  get counterString() {
    return `Page ${this._currentPageNumber} of ${this.swatchPagesCount}`;
  }

  updateCurrentPatternSwatches() {
    const results = this._loadedPages[this._currentPagePath]?.results || [];
    if (results.length) {
      this._currentPatternSwatches = results;
      this._isLoadingCurrentResults = false;
      if (!this._hasLoadedInitialOptions) {
        this.detectUrlDirectOpen();
        this._hasLoadedInitialOptions = true;
      }
    }
    if (
      this._userInvokedSearch &&
      this._reportedSearchString !== this._searchString
    ) {
      this.sendAdobeSearchEvent();
    }
  }

  sendAdobeSearchEvent() {
    if (this._doingSearchLookup) {
      this._doingSearchLookup = false;
      setTimeout(() => {
        this._userInvokedSearch = false;
        const resultsFound = !!this._currentPatternSwatches.length;
        const eventData = {
          type: 'tool',
          detail: {
            search: {
              term: this._searchString,
              results: resultsFound,
              toolName: 'Visualization',
            },
          },
        };
        fireAdobeAnalyticsEvent(eventData);
        this._reportedSearchString = this._searchString;
      }, 200);
    }
  }

  async fetchThreekitHardwareOptions() {
    this._isLoadingCurrentResults = true;
    // use Varia as a default selected material id if one isn't chosen
    const selectedMaterialId = this._selectedMaterial
      ? this._selectedMaterial.id
      : 2;
    const hardwareFinishesPath =
      this.content.threekit_hardware_finishes_path +
      `&sample_type_id=${selectedMaterialId}`;

    fetch(hardwareFinishesPath, getInit)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .then(async json => {
        this.setHardwareOptions(json);
        await this.fetchThreekitVariants();
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  updateSearchSelection(inputValue, usedEmbeddedSearch = false) {
    if (inputValue === this._searchString) return;
    this._currentPatternSwatches = [];
    if (inputValue.length !== 1) {
      if (!usedEmbeddedSearch) this.clearFilterSelections();
      this._searchString = inputValue;
    }
    this.fetchThreekitVariants();
  }

  clearFilterSelections() {
    this._filterUrlQueryString = '';
    const clearFilterSelections = new CustomEvent('clearFilterSelections', {
      detail: {
        clear: true,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(clearFilterSelections);
  }

  updateFilterSelection(event = {}) {
    this._currentPatternSwatches = [];
    this._searchString = '';
    this._currentPageNumber = 1;
    this.requestUpdate();

    if (typeof event.detail.selectedFiltersString === 'string') {
      this._filterUrlQueryString = event?.detail?.selectedFiltersString;
    }

    this.fetchThreekitVariants();
  }

  setHardwareOptions(data) {
    const hardwareFinishOptions = data?.threekit_hardware_finishes || [];
    this._hardwareOptions = [];

    if (hardwareFinishOptions.length) {
      data.threekit_hardware_finishes.forEach(finish => {
        if (finish.display_name && finish.color) {
          finish['isStandard'] = finish.color.toLowerCase() === 'c5c5c5';
          if (
            // Black Oxide is standard only for Suspend
            this.content.threekit_stage_id === 13 &&
            finish.color.toLowerCase() === '0d0d0d'
          ) {
            finish['isStandard'] = true;
          }
          finish['needsBorder'] = finish.display_name
            .toLowerCase()
            .includes('white');
          this._hardwareOptions.push(finish);
        }
      });
    }

    // Products that may not have any displayable hardware finishes (Hush Clad)
    if (
      hardwareFinishOptions?.length === 1 &&
      !hardwareFinishOptions[0].color
    ) {
      this.hideHardwareOptions = true;
      this.updateSelectedHardwareFinish(hardwareFinishOptions[0]);
    }

    if (this._hardwareOptions.length > 0) {
      // Sort the isStandard finishes to the front of the array
      this._hardwareOptions.sort((a, b) => {
        return (b.isStandard === true) - (a.isStandard === true);
      });

      this.updateSelectedHardwareFinish(this._hardwareOptions[0]);
    }
  }

  async fetchThreekitVariants(pageNumber = null) {
    this._isLoadingCurrentResults = true;
    let [basePath, baseQueryParams] = this.content.page_variants_path.split(
      '?'
    );

    if (this._selectedMaterial) {
      basePath = basePath.replace(
        ':sample_type_slug',
        this._selectedMaterial.slug
      );
    }

    const threekitParams = new URLSearchParams(baseQueryParams);
    threekitParams.set(
      'threekit_hardware_finish_id',
      this._selectedHardwareFinish.id
    );

    // If a user is coming from the visualization cta of a material page
    // the first item in this list should be the material they came from.
    // This is handled in the window's url and passed into the variantUrl here
    const renderInterlayerId =
      new URLSearchParams(window.location.search).get('render_interlayer_id') ||
      null;
    if (renderInterlayerId) {
      threekitParams.set('featured_render_interlayer_id', renderInterlayerId);
      if (!this._selectedFirstOptionOnLoad) {
        this._selectFirstOptionOnLoad = true;
        this._selectedFirstOptionOnLoad = true;
      }
    }

    if (pageNumber) threekitParams.set('page', pageNumber);
    if (window.useMobileLayout) threekitParams.set('perPage', '10');

    let variantUrl = `${basePath}?${threekitParams}`;
    if (this._searchString.length) {
      variantUrl += `&searchTerm=${this._searchString}`;
      this._doingSearchLookup = true;
    } else if (this._filterUrlQueryString !== undefined) {
      variantUrl += `${this._filterUrlQueryString}`;
    }

    this._loadedPages = {};
    this._currentPagePath = variantUrl;

    await this.loadVariantPage(variantUrl)
      .then(json => {
        if (typeof json === 'object') {
          this._totalPages = json.total_pages;

          this.setVariantOptions(json, variantUrl);
          this._filterOptions = json?.categories || [];

          this.loadVariantPage(json.next_page_path).then(nextPage => {
            this.setVariantOptions(nextPage, json.next_page_path);
          });

          this.loadVariantPage(json.previous_page_path).then(previousPage => {
            this.setVariantOptions(previousPage, json.previous_page_path);
          });
        }
      })
      .then(() => {
        this.updateCurrentPatternSwatches();
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  loadVariantPage(variantPagePath) {
    return fetch(variantPagePath, getInit)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  setVariantOptions(data, path) {
    this._loadedPages[path] = data;
    if (this._selectFirstOptionOnLoad) {
      this.updateSelectedPattern(this._loadedPages[path]?.results[0]);
      this._selectFirstOptionOnLoad = false;
    }
  }

  detectUrlDirectOpen() {
    if (window.location.hash.includes('#design-your-own')) {
      const urlScrollToVisualize = new CustomEvent('urlScrollToVisualize', {
        bubbles: true,
        composed: true,
      });
      const el = this.shadowRoot.getElementById('visualization-barrel-header');
      el.dispatchEvent(urlScrollToVisualize);
    }
  }

  sendBarrelViewAnalyticsEvent() {
    if (typeof gtag === 'function') {
      const productName = this.content.headerStr;
      gtag('event', `Visualization Barrel Opened`, {
        event_category: 'Visualization',
        event_label: productName,
      });
    }
  }

  sendBarrelRequestQuoteAnalyticsEvent() {
    if (typeof gtag === 'function') {
      const productName = this.content.headerStr || '';
      const patternName = this._selectedPattern?.name || 'none_selected';
      const hardwareFinishName =
        this._selectedHardwareFinish?.display_name || 'none_selected';
      gtag('event', `Visualization Quote Request`, {
        event_category: 'Visualization',
        visualized_product: productName,
        visualized_pattern: patternName,
        visualized_hardware: hardwareFinishName,
      });
    }
  }
}
customElements.define('customize-product-barrel', CustomizeProductBarrel);
